import { toPng } from 'html-to-image';
import { formatCategoryLabel, filterProtocolsByStatusCategory } from './sortUtils';

/**
 * Obfuscates a supplier or brand name by showing only the first letter
 * and replacing all subsequent characters with asterisks.
 * Example: "NEXAPH" -> "N*****"
 */
export function obfuscateSupplierName(supplier) {
  if (!supplier || typeof supplier !== 'string') return '';
  const trimmed = supplier.trim();
  if (trimmed.length <= 1) return trimmed;
  return trimmed[0] + '*'.repeat(trimmed.length - 1);
}

/**
 * Filter protocol records based on status, categories, and sort options
 */
export function filterProtocolsForExport(protocols = [], options = {}) {
  return filterProtocolsByStatusCategory(protocols, options);
}

/**
 * Generate a filename for image exports matching format like:
 * "All_Compounds_Active.png", "Supplements_Hormones_Active.png", or "Tadalafil.png"
 */
export function buildImageFilename(options = {}, singleProtocol = null) {
  if (singleProtocol) {
    const rawName = singleProtocol.name || 'Protocol';
    const safeName = rawName
      .trim()
      .replace(/\s+/g, '_')
      .replace(/[^a-zA-Z0-9_\-]/g, '');
    return `${safeName || 'Protocol'}.png`;
  }

  const categoryLabel = formatCategoryLabel(options.categories, options.category);
  const safeCategory = categoryLabel
    .trim()
    .replace(/&/g, '')
    .replace(/\s+/g, '_')
    .replace(/__+/g, '_');
  const safeStatus = (options.status || 'Active').trim().replace(/\s+/g, '_');

  return `${safeCategory}_${safeStatus}.png`;
}

/**
 * Capture a DOM element as a non-transparent high quality PNG image
 */
export async function captureElementAsPng(element) {
  if (!element) {
    throw new Error('Element not found for image capture');
  }

  const width = element.offsetWidth || 960;
  const height = element.offsetHeight || 600;

  // toPng options to ensure crisp non-transparent rendering
  const dataUrl = await toPng(element, {
    backgroundColor: '#090d16', // Solid dark theme background (non-transparent)
    pixelRatio: 2,               // Crisp high DPI output
    cacheBust: true,
    width: width,
    height: height,
    style: {
      opacity: '1',
      visibility: 'visible',
      transform: 'none',
      left: '0',
      top: '0',
      position: 'relative'
    },
    filter: (node) => {
      if (node.classList && node.classList.contains('no-export')) {
        return false;
      }
      return true;
    }
  });

  return dataUrl;
}

/**
 * Download a data URL as an image file
 */
export function downloadImageDataUrl(dataUrl, filename = 'All_Compounds_Active.png') {
  const link = document.createElement('a');
  link.download = filename;
  link.href = dataUrl;
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Copy an image data URL to the system clipboard
 */
export async function copyImageDataUrlToClipboard(dataUrl) {
  try {
    const response = await fetch(dataUrl);
    const blob = await response.blob();

    if (navigator.clipboard && window.ClipboardItem) {
      await navigator.clipboard.write([
        new ClipboardItem({
          [blob.type]: blob
        })
      ]);
      return { success: true };
    } else {
      throw new Error('Clipboard API not supported in this browser environment');
    }
  } catch (err) {
    console.error('Failed to copy image to clipboard:', err);
    return { success: false, error: err.message };
  }
}
